import { NgFor, NgIf } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  TrackByFunction,
  untracked
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { EneLayoutService } from "src/app/core/services/layout.service";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { DialogFrameModule } from "src/app/common/components/dialog-frame/dialog-frame.module";
import { GlossaryItem } from "src/app/common/dto/glossary";
import { AnalyticsService } from "src/app/core/services/analytics/analytics.service";
import { ChipSetComponent } from "src/app/shared/ui/chip-set/chip-set.component";
import { TextFieldComponent } from "src/app/shared/ui/form-fields/text-field/text-field.component";
import { TabsComponent } from "src/app/shared/ui/tabs/tabs.component";
import { GlossaryDialogComponentStore } from "./glossary-dialog.component-store";

const tabsMap = {
  regulated: [
    { name: "definitions", label: "GLOSSARY_DIALOG.tabs.definitions" },
    { name: "moduleKnowledge", label: "GLOSSARY_DIALOG.tabs.moduleKnowledge" }
  ],
  marketplace: [
    { name: "explanationOfTerms", label: "GLOSSARY_DIALOG.tabs.explanationOfTerms" },
    { name: "calculationLogic", label: "GLOSSARY_DIALOG.tabs.calculationLogic" },
    { name: "datasources", label: "GLOSSARY_DIALOG.tabs.datasources" }
  ]
};

const filtersMap = {
  regulated: ["module-00005", "module-00006", "module-00007", "module-00008"].map((moduleId) => ({
    label: `GLOSSARY_DIALOG.longModuleNamesMap.${moduleId}`,
    value: moduleId
  })),
  marketplace: []
};

@Component({
  selector: "tpm-glossary-dialog",
  templateUrl: "glossary-dialog.component.html",
  styleUrls: ["glossary-dialog.component.scss"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgFor,
    NgIf,
    FormsModule,
    DialogFrameModule,
    TranslateModule,
    TabsComponent,
    TextFieldComponent,
    ChipSetComponent
  ],
  providers: [GlossaryDialogComponentStore]
})
export class GlossaryDialogComponent {
  private readonly dialogData = inject<{ type: "regulated" | "marketplace"; tab?: string }>(MAT_DIALOG_DATA);
  private readonly componentStore = inject(GlossaryDialogComponentStore);
  private readonly layoutService = inject(EneLayoutService);
  private readonly translate = inject(TranslateService);
  private readonly analytics = inject(AnalyticsService);

  readonly tabs = tabsMap[this.dialogData.type];
  readonly filters = filtersMap[this.dialogData.type];

  readonly isMobile = toSignal(this.layoutService.isMobileScreen$);

  readonly result = toSignal(this.componentStore.result$);
  readonly loading = toSignal(this.componentStore.loading$);

  readonly search = signal<string>("");
  readonly activeTab = signal<string>(this.dialogData.tab || this.tabs[0].name);
  readonly activeFilters = signal<Array<string>>([]);

  readonly list = computed(() => {
    if (!this.result()) {
      return [];
    } else {
      const list: Array<GlossaryItem> = this.result()[this.activeTab()];

      if (!list) {
        return [];
      } else {
        const filterStr = this.activeFilters().join("◬");
        const searchStr = this.search().trim().toLowerCase();

        return (
          list
            // filter by tags
            .filter((item) => !filterStr || item.tags.some((tag) => filterStr.includes(tag)))
            // translate for searching
            .map((item) => ({
              title: this.translate.instant(item.title),
              content: this.translate.instant(item.content),
              tags: item.tags.map((tag) => this.translate.instant(`GLOSSARY_DIALOG.shortModuleNamesMap.${tag}`))
            }))
            // filter by search term
            .filter((item) => `${item.title}${item.content}`.toLowerCase().includes(searchStr))
            // sort alphabetically
            .sort((a, b) => a.title.localeCompare(b.title))
        );
      }
    }
  });

  readonly trackByFn: TrackByFunction<GlossaryItem> = (index) => index;

  constructor() {
    effect(() => {
      const tab = this.activeTab();

      untracked(() => {
        this.analytics.trackGlossaryTabClick(tab);
      });
    });

    this.componentStore.loadList({ type: this.dialogData.type });
  }
}
